import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, X, Search, Filter } from 'lucide-react';
import { Card, Badge, Button } from '../../components/ui';
import { api } from '../../lib/api';

interface TenantCategory {
  id: string;
  tenantId: string;
  tenantName: string;
  category: string;
  isEnabled: boolean;
  emissionLimit: number;
  currentEmission: number;
  enabledAt?: string;
}

interface Tenant {
  id: string;
  name: string;
  cnpj: string;
  status: string;
}

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundos',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios'
};

const categories = Object.keys(categoryLabels);

export function TenantCategoryAccess() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const { data: tenants, isLoading: tenantsLoading } = useQuery({
    queryKey: ['tenants-list'],
    queryFn: async () => {
      const res = await api.get<Tenant[]>('/super-admin/tenants');
      return res.data;
    }
  });

  const { data: tenantCategories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['tenant-categories'],
    queryFn: async () => {
      const res = await api.get<TenantCategory[]>('/tokenization/tenant-categories');
      return res.data;
    }
  });

  const toggleMutation = useMutation({
    mutationFn: ({ tenantId, category, enable, emissionLimit }: { 
      tenantId: string; 
      category: string; 
      enable: boolean;
      emissionLimit?: number;
    }) => {
      if (enable) {
        return api.post('/tokenization/tenant-categories', { tenantId, category, emissionLimit });
      } else {
        return api.delete(`/tokenization/tenant-categories/${tenantId}/${category}`);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-categories'] })
  });

  const updateLimitMutation = useMutation({
    mutationFn: ({ tenantId, category, emissionLimit }: { 
      tenantId: string; 
      category: string; 
      emissionLimit: number;
    }) => api.put(`/tokenization/tenant-categories/${tenantId}/${category}`, { emissionLimit }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-categories'] })
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      notation: 'compact'
    }).format(value / 100);
  };

  const getCategoryStatus = (tenantId: string, category: string) => {
    return tenantCategories?.find(tc => tc.tenantId === tenantId && tc.category === category);
  };

  const filteredTenants = tenants?.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.cnpj.includes(searchTerm)
  );

  const isLoading = tenantsLoading || categoriesLoading;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-800 rounded w-64" />
          <div className="h-96 bg-surface-800 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-surface-100">Acesso por Categoria</h1>
        <p className="text-surface-400">Gerencie quais categorias de tokenização cada tenant pode acessar</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
          <input
            type="text"
            placeholder="Buscar tenant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-surface-800 border border-surface-700 rounded-lg pl-10 pr-4 py-2 text-surface-100"
          />
        </div>
        <select
          value={selectedCategory || ''}
          onChange={(e) => setSelectedCategory(e.target.value || null)}
          className="bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
        >
          <option value="">Todas as categorias</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{categoryLabels[cat]}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-800/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Tenant</th>
                {(selectedCategory ? [selectedCategory] : categories).map(cat => (
                  <th key={cat} className="text-center px-4 py-3 text-sm font-medium text-surface-400">
                    {categoryLabels[cat]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-800">
              {filteredTenants?.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-surface-800/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-surface-700 rounded-lg">
                        <Building2 className="w-4 h-4 text-surface-400" />
                      </div>
                      <div>
                        <p className="font-medium text-surface-100">{tenant.name}</p>
                        <p className="text-xs text-surface-500">{tenant.cnpj}</p>
                      </div>
                    </div>
                  </td>
                  {(selectedCategory ? [selectedCategory] : categories).map(cat => {
                    const status = getCategoryStatus(tenant.id, cat);
                    return (
                      <td key={cat} className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <button
                            onClick={() => toggleMutation.mutate({
                              tenantId: tenant.id,
                              category: cat,
                              enable: !status?.isEnabled,
                              emissionLimit: 100000000 * 100 // R$ 100M default
                            })}
                            disabled={toggleMutation.isPending}
                            className={`p-2 rounded-lg transition-colors ${
                              status?.isEnabled
                                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                : 'bg-surface-700 text-surface-500 hover:bg-surface-600'
                            }`}
                          >
                            {status?.isEnabled ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                          </button>
                          {status?.isEnabled && (
                            <div className="text-xs">
                              <span className="text-surface-400">
                                {formatCurrency(status.currentEmission)} / {formatCurrency(status.emissionLimit)}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm text-surface-400">
        <div className="flex items-center gap-2">
          <div className="p-1 bg-green-500/20 rounded">
            <Check className="w-3 h-3 text-green-400" />
          </div>
          <span>Habilitado</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="p-1 bg-surface-700 rounded">
            <X className="w-3 h-3 text-surface-500" />
          </div>
          <span>Desabilitado</span>
        </div>
      </div>
    </div>
  );
}
